/**
 * A gentle human-check for the public "claim this monastery" form.
 *
 * This is not a hard CAPTCHA and does not try to be — no puzzle images, no
 * third-party script, nothing that punishes a person using a screen reader. It
 * is one plain multiple-choice question that anyone connected to a monastery
 * answers without thinking, and that a drive-by spam script won't bother to.
 * It is a dropdown, not a text box, so there is nothing to misspell. Paired
 * with the honeypot field already on the form, it is enough to keep the staff
 * review queue clean.
 *
 * The correct answer for each id is mirrored on the server
 * (server/src/lib/dharmaChallenge.js) so a bot that skips the browser still has
 * to pick it. Keep the `answer` strings in sync across the two files.
 */

export const DHARMA_CHALLENGES = [
  {
    id: 'mani',
    prompt: 'Complete the mantra of Chenrezig: “Om mani padme ___”',
    answer: 'Hum',
    options: ['Hum', 'Buddha', 'Lotus', 'Peace'],
  },
  {
    id: 'jewels',
    prompt: 'The Three Jewels are the Buddha, the Dharma, and the ___',
    answer: 'Sangha',
    options: ['Sangha', 'Guru', 'Temple', 'Mantra'],
  },
  {
    id: 'bodhi',
    prompt: 'The Buddha reached awakening sitting beneath which tree?',
    answer: 'Bodhi tree',
    options: ['Bodhi tree', 'Oak tree', 'Sal tree', 'Banyan tree'],
  },
  {
    id: 'truths',
    prompt: 'How many Noble Truths did the Buddha teach?',
    answer: 'Four',
    options: ['Four', 'Two', 'Six', 'Eight'],
  },
  {
    id: 'lotus',
    prompt: 'Which flower, rooted in mud but blooming unstained, is the Buddhist symbol of purity?',
    answer: 'Lotus',
    options: ['Lotus', 'Rose', 'Jasmine', 'Marigold'],
  },
  {
    id: 'refuge',
    prompt: 'To formally become Buddhist, one takes ___ in the Buddha, Dharma, and Sangha',
    answer: 'Refuge',
    options: ['Refuge', 'Vows', 'Merit', 'Rebirth'],
  },
];

const shuffled = (list) => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

/** Pick one question at random, with its options in a random order so the
 * answer is not always in the same slot. */
export const pickDharmaChallenge = () => {
  const chosen = DHARMA_CHALLENGES[Math.floor(Math.random() * DHARMA_CHALLENGES.length)];
  return { ...chosen, options: shuffled(chosen.options) };
};

export const checkDharmaAnswer = (id, value) => {
  const challenge = DHARMA_CHALLENGES.find((c) => c.id === id);
  return !!challenge && String(value || '').trim() === challenge.answer;
};
